export type CustomerRegisterLinkInput = {
  email?: string | null;
  name?: string | null;
  phone?: string | null;
  address?: string | null;
  postalCode?: string | null;
  city?: string | null;
  customerType?: 'private' | 'business' | null;
  organizationNumber?: string | null;
  billingAddress?: string | null;
  billingPostalCode?: string | null;
  billingCity?: string | null;
  billingEmail?: string | null;
  invoiceReference?: string | null;
  contactName?: string | null;
};

export type FindOrCreateCustomerArgs = {
  p_email: string;
  p_name: string | null;
  p_phone: string | null;
  p_address: string | null;
  p_postal_code: string | null;
  p_city: string | null;
  p_customer_type: 'private' | 'business';
  p_organization_number: string | null;
  p_billing_address: string | null;
  p_billing_postal_code: string | null;
  p_billing_city: string | null;
  p_billing_email: string | null;
  p_invoice_reference: string | null;
  p_contact_name: string | null;
};

export type InvoiceBillingInput = {
  companyName?: string | null;
  organizationNumber?: string | null;
  billingAddress?: string | null;
  billingPostalCode?: string | null;
  billingCity?: string | null;
  billingEmail?: string | null;
  invoiceReference?: string | null;
};

function normalizeEmail(email: string | null | undefined): string | null {
  const trimmed = String(email || '').trim();
  if (!trimmed || !trimmed.includes('@')) return null;
  return trimmed;
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
}

export function buildFindOrCreateCustomerArgs(
  input: CustomerRegisterLinkInput,
): FindOrCreateCustomerArgs | null {
  const email = normalizeEmail(input.email);
  if (!email) return null;

  const isBusiness = input.customerType === 'business';
  const name = blankToNull(input.name);
  const contactName = blankToNull(input.contactName);
  const contact =
    isBusiness && contactName && contactName.toLowerCase() !== (name || '').toLowerCase()
      ? contactName
      : null;

  return {
    p_email: email,
    p_name: name,
    p_phone: blankToNull(input.phone),
    p_address: blankToNull(input.address),
    p_postal_code: blankToNull(input.postalCode),
    p_city: blankToNull(input.city),
    p_customer_type: isBusiness ? 'business' : 'private',
    p_organization_number: isBusiness ? blankToNull(input.organizationNumber) : null,
    p_billing_address: isBusiness ? blankToNull(input.billingAddress) : null,
    p_billing_postal_code: isBusiness ? blankToNull(input.billingPostalCode) : null,
    p_billing_city: isBusiness ? blankToNull(input.billingCity) : null,
    p_billing_email: isBusiness ? blankToNull(input.billingEmail) : null,
    p_invoice_reference: isBusiness ? blankToNull(input.invoiceReference) : null,
    p_contact_name: contact,
  };
}

export function legacyFindOrCreateCustomerArgs(args: FindOrCreateCustomerArgs) {
  return {
    p_email: args.p_email,
    p_name: args.p_name,
    p_phone: args.p_phone,
    p_address: args.p_address,
    p_postal_code: args.p_postal_code,
    p_city: args.p_city,
  };
}

export function isUnknownCustomerRpcArg(message: string): boolean {
  return /p_customer_type|p_organization_number|p_contact_name|p_billing_|p_invoice_reference|schema cache/i.test(
    message,
  );
}

export function invoiceBillingError(input: InvoiceBillingInput): string | null {
  const companyName = String(input.companyName ?? '').trim();
  const orgDigits = String(input.organizationNumber ?? '').replace(/\D/g, '');
  const address = String(input.billingAddress ?? '').trim();
  const postal = String(input.billingPostalCode ?? '').trim();
  const city = String(input.billingCity ?? '').trim();
  const email = String(input.billingEmail ?? '').trim();
  const reference = String(input.invoiceReference ?? '').trim();

  if (!companyName) return 'Ange företagsnamn för fakturan.';
  if (orgDigits.length < 10) return 'Ange organisationsnummer för fakturan.';
  if (!address || !postal || !city) return 'Ange fakturaadress, postnummer och ort.';
  if (!email.includes('@')) return 'Ange e-post för fakturan.';
  if (!reference) return 'Ange er referens för fakturan.';
  return null;
}
